import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { LoginGlobalError, LoginGlobalInfo, URLParameters } from '../../../types';
import { EventAttributes } from '../../../utilities/analytics/analytics-web-client';
import { lookupApplication } from '../../../utilities/applications';
import { getEmailFromLoginHint, useLoginHint } from '../../../utilities/hooks/login-hint';
import { useAppSelector } from '../../../stores/redux.store';
import { useRegisterComponent } from '../../orchestrator';

export type LoginRecoveryOptionsType =
  | {
      expectedEmail: string;
      recoveryToken: string;
    }
  | undefined;

export const useLoginRecoveryOptions = () => {
  const login = useAppSelector(state => state.login);
  const loginRecoveryOptions = useCallback(
    (email: string): LoginRecoveryOptionsType => {
      return login?.recoveryToken && email
        ? { expectedEmail: email, recoveryToken: login.recoveryToken }
        : undefined;
    },
    [login?.recoveryToken]
  );

  return loginRecoveryOptions;
};

export const useCobrandingApplication = () => {
  const [searchParams] = useSearchParams();
  const applicationData = useMemo(() => lookupApplication(searchParams), [searchParams]);

  return applicationData?.coreApplication;
};

export const useSearchParamCodes = () => {
  const [searchParams] = useSearchParams();

  const queryParams: URLParameters = Object.fromEntries(searchParams.entries());
  const infoCode = useMemo(
    () => (queryParams.infoCode ? ({ code: queryParams.infoCode } as LoginGlobalInfo) : null),
    [queryParams.infoCode]
  );

  const errorCode = useMemo(
    () => (queryParams.errorCode ? ({ code: queryParams.errorCode } as LoginGlobalError) : null),
    [queryParams.errorCode]
  );

  return {
    errorCode,
    infoCode,
  };
};

export const useEmailFromSearchParams = () => {
  useRegisterComponent(useEmailFromSearchParams.name);

  const loginHint = useLoginHint();
  const [queryParams] = useSearchParams();
  return getEmailFromLoginHint(loginHint) || queryParams.get('email') || '';
};

export const useAppendReasonViewingScreen = () => {
  const [searchParams] = useSearchParams();

  const infoCode = useMemo(() => {
    return searchParams.has('infoCode')
      ? ({ code: searchParams.get('infoCode') } as LoginGlobalInfo)
      : null;
  }, [searchParams]);

  const appendReasonViewingScreen = useCallback(
    (eventAttributes: EventAttributes) => {
      if (infoCode) {
        switch (infoCode.code) {
          case 'migratingExistingUser':
            return {
              ...eventAttributes,
              reasonViewingScreen: 'migratingUser',
            };
          case 'existingUserSignupAttempt':
            return {
              ...eventAttributes,
              reasonViewingScreen: 'existingUserSignupAttempt',
            };
          case 'invitedUser':
            return {
              ...eventAttributes,
              reasonViewingScreen: 'invitedUser',
            };
          default:
            return eventAttributes;
        }
      }
      return eventAttributes;
    },
    [infoCode]
  );

  return appendReasonViewingScreen;
};
